import React, { useState, useEffect, useRef } from "react";
import { FaDownload, FaUpload, FaTrash, FaUndo } from "react-icons/fa";
import axios from "axios";
import "./Vehicle.css";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import {FaPlus } from 'react-icons/fa';
import useAuth from "../../Auth/useAuth";

const getVehicleDataUrl = `${config.BASE_URL}/list-vehicle`;
const insertVehicleDataUrl = `${config.BASE_URL}/add-vehicle`;
const uploadDocumentUrl = `${config.BASE_URL}/upload`;
const deleteVehicleDataUrl = `${config.BASE_URL}/delete-vehicle`;
const resetVehicleDocUrl = `${config.BASE_URL}/reset-vehicle-doc`;

// approval request URLs
const createRequestUrl = `${config.BASE_URL}/create-request`;

const Vehicle = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    truck_no: "",
    make: "",
    companyOwner: "",
    fields: [
      { name: "registration", file: null, startDate: "", endDate: "" },
      { name: "insurance", file: null, startDate: "", endDate: "" },
      { name: "fitness", file: null, startDate: "", endDate: "" },
      { name: "mv_tax", file: null, startDate: "", endDate: "" },
      { name: "puc", file: null, startDate: "", endDate: "" },
      { name: "ka_tax", file: null, startDate: "", endDate: "" },
      { name: "basic_and_KA_permit", file: null, startDate: "", endDate: "" },
    ],
  });
  const fileInputRef = useRef([]);
  const [result, setResult] = useState([]);
  const [submitMessage, setSubmitMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadFields, setUploadFields] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeUploadField, setActiveUploadField] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileErrors, setFileErrors] = useState("");
  const [progress, setProgress] = useState({});
  const [dateError, setDateError] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recordId: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const { username, userProfile, setActiveModule } = useAuth();

  // Approval request state
  const [approvalRequest, setApprovalRequest] = useState({
    module: "Vehicle",
    username: username,
    profile: userProfile,
    action: "",
    args: {},
  });

  const keyDisplayMap = {
    registration: "Registration",
    insurance: "Insurance",
    fitness: "Fitness",
    mv_tax: "MV Tax",
    puc: "PUC",
    ka_tax: "KA Tax",
    basic_and_KA_permit: "Basic & KA",
  };

  // reset states
  const [resetModal, setResetModal] = useState({
    isOpen: false,
    truckNo: null,
    docKey: null,
  });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

    // Modal handler functions
  const showModal = (title, message, type = 'info') => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

    const openAddModal = () => {
    // Reset form data when opening modal
    setFormData({
      truck_no: "",
      make: "",
      companyOwner: "",
      fields: formData.fields.map((field) => ({
        ...field,
        startDate: "",
        endDate: "",
        file: null,
      })),
    });
    setErrorMessage("");
    setDateError({});
    setFileErrors({});
    setSubmitMessage("");
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    // Clear file inputs
    if (fileInputRef.current) {
      fileInputRef.current.forEach((input) => {
        if (input) input.value = "";
      });
    }
  };


  useEffect(() => {
    sessionStorage.setItem("actMod", "vehicle");
    setActiveModule(sessionStorage.getItem("actMod"));
  }, [setActiveModule]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(getVehicleDataUrl, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response?.data && Array.isArray(response.data)) {
        // Transform null values to empty strings
        const cleanedData = response.data.map((vehicle) => ({
          ...vehicle,
          companyOwner:
            vehicle.companyOwner !== null ? vehicle.companyOwner : "",
          make: vehicle.make !== null ? vehicle.make : "",
        }));
        setResult(cleanedData);
      }
    } catch (error) {
      console.error("Error fetching vehicles data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "truck_no") {
      // Convert to uppercase and remove spaces
      const truckNo = value.toUpperCase().replace(/\s+/g, "");

      // Define the fixed pattern for truck numbers
      const truckNoPattern = /^[A-Z]{2}\d{2}[A-Z]{2}\d{4}$/;

      if (!truckNoPattern.test(truckNo)) {
        setErrorMessage("Use format: MH12KL1111");
        setFormData({ ...formData, [name]: truckNo }); // Update input field
        return; // Stop further execution if format is incorrect
      }

      // Check if the entered truck_no already exists in filteredVehicles
      const check_truck = filteredVehicles.some(
        (vehicle) => vehicle.truck_no.toUpperCase() === truckNo
      );

      if (check_truck) {
        setErrorMessage("Truck already exists.");
      } else {
        setErrorMessage("");
      }

      setFormData({ ...formData, [name]: truckNo });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFieldChange = (index, field, value) => {
    setFormData((prevState) => {
      const updatedFields = [...prevState.fields];

      if (field === "file" && value) {
        const fieldId = `field-${index}`;
        const fileInput = document.getElementById(fieldId);

        if (value.type && value.type !== "application/pdf") {
          setFileErrors((prev) => ({
            ...prev,
            [fieldId]: "File type should be .pdf only",
          }));
          if (fileInput) fileInput.value = "";
          return prevState; // Prevent further state updates
        }

        if (value.size && value.size > 1 * 1024 * 1024) {
          setFileErrors((prev) => ({
            ...prev,
            [fieldId]: "File size should be 1MB or less",
          }));
          if (fileInput) fileInput.value = "";
          return prevState;
        }

        setFileErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });

        updatedFields[index] = { ...updatedFields[index], file: value };
      }

      if (field === "startDate" || field === "endDate") {
        updatedFields[index] = { ...updatedFields[index], [field]: value };

        const startDate = updatedFields[index].startDate;
        const endDate = updatedFields[index].endDate;

        if ((startDate && !endDate) || (!startDate && endDate)) {
          setDateError((prev) => ({
            ...prev,
            [`field-${index}`]: "Both start and end dates should be provided.",
          }));
        } else if (
          startDate &&
          endDate &&
          new Date(endDate) < new Date(startDate)
        ) {
          setDateError((prev) => ({
            ...prev,
            [`field-${index}`]:
              "End date should be greater than or equal to the start date.",
          }));
        } else {
          setDateError((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`field-${index}`];
            return newErrors;
          });
        }
      }

      return {
        ...prevState,
        fields: updatedFields,
      };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setProgress({});

    let isValid = true;
    const errors = {};

    formData.fields.forEach((field, index) => {
      const fieldId = `field-${index}`;
      const hasFile = !!field.file;
      const hasStartDate = !!field.startDate;
      const hasEndDate = !!field.endDate;

      // Case 1: If a file is uploaded, both dates must be provided
      if (hasFile && (!hasStartDate || !hasEndDate)) {
        errors[fieldId] =
          "Both start and end dates are required when a file is uploaded.";
        isValid = false;
      }

      // Case 2: If either date is provided, a file must be uploaded
      if ((hasStartDate || hasEndDate) && !hasFile) {
        errors[fieldId] =
          "A file must be uploaded when start and end dates are provided.";
        isValid = false;
      }

      // If all three are present, remove any existing error
      if (hasFile && hasStartDate && hasEndDate) {
        delete errors[fieldId];
      }
    });

    if (!isValid) {
      setDateError(errors);
      return;
    }

    
    const formDataToSend = new FormData();
    formDataToSend.append("truck_no", formData.truck_no.trim());
    formDataToSend.append("make", formData.make);
    formDataToSend.append("companyOwner", formData.companyOwner);

    const fieldNames = {};
    formData.fields.forEach((f) => {
      if (f.file) {
        formDataToSend.append(f.name, f.file);
        fieldNames[f.name] = 0;
      }
      formDataToSend.append(`${f.name}_startDate`, f.startDate || "");
      formDataToSend.append(`${f.name}_endDate`, f.endDate || "");
    });

    console.log('FormData: ', formData);
    console.log('FormDataToSend ', formDataToSend);

    if (userProfile === "admin") {
      console.log("Admin user, adding vehicle details directly.");
      try {
        const response = await axios.post(
          insertVehicleDataUrl,
          formDataToSend,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setProgress((prevProgress) => ({
                ...prevProgress,
                ...Object.keys(fieldNames).reduce((acc, name) => {
                  acc[name] = percentCompleted;
                  return acc;
                }, {}),
              }));
            },
          }
        );

        console.log("Response:", response.data);
        setSubmitMessage("Vehicle Details added successfully");
        fetchData();
        // Close modal after successful submission
        closeAddModal();
      } catch (error) {
        console.error("Error submitting data:", error);
        setSubmitMessage("Error: Adding vehicle details failed");
        setProgress({});
      }
    }else {
      // Non-admin: send approval request with files via multipart/form-data
      console.log("User is not an admin.>>>", formData);

      try {
        // Build args in the flat shape CreateApproval expects

        //Although we can store the textual details like teh truck_no, make, companyOwner, startDate, endDate directly in the 
        //form object instead of the args, so that could have been directly extractable in the req.body in the approval controller
        //but the thing is the approval controller is generic for various approval creation operations, thats why it may lead to 
        //inconsistancy for it to handle some of the textual details only thr the req.body rather than the args which is the case
        //for other approval creation operations.
        const args = {
          truck_no: formData.truck_no.trim(),
          make: formData.make,
          companyOwner: formData.companyOwner,
        };

        formData.fields.forEach((f) => {
          const startKey = `${f.name}_startDate`;
          const endKey = `${f.name}_endDate`;
          args[startKey] = f.startDate || "";
          args[endKey] = f.endDate || "";
        });

        const approvalPayload = {
          ...approvalRequest,
          action: "Add",
          args,
        };
        console.log("approvalPayload>>>", approvalPayload);
        setApprovalRequest(approvalPayload); // Optional: for UI tracking

        // Build multipart FormData for approval + files
        const fd = new FormData();
        // For multer (destination needs truck_no)
        fd.append("truck_no", formData.truck_no.trim());
        // Optional: keep these for debugging / future use
        fd.append("make", formData.make);
        fd.append("companyOwner", formData.companyOwner);

        // Attach files with the same field names used in /add-vehicle
        formData.fields.forEach((f) => {
          if (f.file) {
            fd.append(f.name, f.file);
          }
        });

        // Serialize approvalRequest for backend CreateApproval
        fd.append("approvalRequest", JSON.stringify(approvalPayload));

        const requestApproval = await axios.post(createRequestUrl, fd, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("Approval request created:", requestApproval.data);
        setApprovalRequest((prev) => ({
          ...prev,
          action: "",
          args: "",
        }));

        if (requestApproval.status === 200) {
          console.log("Vehicle add - request sent for approval:", formData);
          setSubmitMessage("Vehicle add - request sent for approval.");
          
          // Close modal after successful request
          closeAddModal();
        }
      } catch (error) {
        console.error("Error while submitting request to add vehicle:", error);
        setSubmitMessage("Vehicle Error! Trying to create duplicate request.");
      }
    }

    setTimeout(() => {
      setSubmitMessage("");
    }, 3000);

    setProgress({});

    setFormData({
      truck_no: "",
      make: "",
      companyOwner: "",
      fields: formData.fields.map((field) => ({
        ...field,
        startDate: "",
        endDate: "",
        file: null,
      })),
    });
    fileInputRef.current.forEach((input) => {
      if (input) input.value = "";
    });
  };

  const handleUploadClick = (truckNo, fieldName) => {
    const fieldKey = `${truckNo}-${fieldName}`;
    setActiveUploadField((prev) => (prev === fieldKey ? null : fieldKey));
  };

  // Update the click outside handler to keep the active upload field open if the click is inside
  const handleClickOutside = (e) => {
    const uploadFields = document.querySelectorAll(".upload-fields");

    const isClickInside = Array.from(uploadFields).some((field) =>
      field.contains(e.target)
    );

    if (!isClickInside) {
      setActiveUploadField(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside); // Use 'mousedown' for better outside detection
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUploadSubmit = async (
    truckNo,
    fieldName,
    file,
    startDate,
    endDate
  ) => {
    const formData = new FormData();
    formData.append("truck_no", truckNo);
    formData.append("field_name", fieldName);
    formData.append("file", file);
    formData.append("start_date", startDate);
    formData.append("end_date", endDate);

    if (userProfile === "admin") {
      console.log("Admin user, uploading file directly.");
      try {
        const response = await axios.post(uploadDocumentUrl, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress((prev) => ({
              ...prev,
              [`${truckNo}-${fieldName}`]: percentCompleted,
            }));
          },
        });
        console.log("Response:", response.data);

        setSubmitMessage("File uploaded successfully!");

        setProgress((prev) => {
          console.log("Resetting Progress State:", {
            ...prev,
            [`${truckNo}-${fieldName}`]: 0,
          });
          return { ...prev, [`${truckNo}-${fieldName}`]: 0 };
        });

        fetchData();
      } catch (error) {
        console.error("Error uploading file:", error);
        showModal("Error", "Error uploading file. Please try again.", "error");
      }
    } else {
      // User is not an admin, show an error message
      console.log("User is not an admin.");

      try {
        const approvalPayload = {
          ...approvalRequest,
          action: "Add File",
          args: {
            truck_no: truckNo,
            field_name: fieldName,
            start_date: startDate,
            end_date: endDate,
            file: file.name, // Or other metadata like size/type
          },
        };

        const fd= new FormData();
        fd.append("truck_no", truckNo);
        fd.append("field_name", fieldName);
        fd.append("file", file);
        fd.append("approvalRequest", JSON.stringify(approvalPayload));

        console.log("approvalPayload>>>", approvalPayload);

        setApprovalRequest(approvalPayload); // Optional: for UI tracking

        const requestApproval = await axios.post(
          createRequestUrl,
          fd,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        console.log("Vehilce: Add File - approval request created:", requestApproval.data);
        if (requestApproval.status === 200) {
          console.log("Vehicle: Add File  - request sent for approval:");
          showModal("Success", "Vehicle: Add File - request sent for approval.", "success");
        }
      } catch (error) {
        console.error("Error add vehicle - approval request failed:", error);
        showModal("Error", "Vehicle: Add File - Error! Trying to create duplicate request.", "error");
      }
    }

    setUploadFields((prev) => ({
      ...prev,
      [`${truckNo}-${fieldName}`]: false,
    }));
  };

  const handleFileChange = (e, truckNo, key) => {
    const file = e.target.files[0];
    const fieldId = `${truckNo}-${key}-file`;

    if (file && file.type !== "application/pdf") {
      setFileErrors((prev) => ({
        ...prev,
        [fieldId]: "File type should be .pdf only",
      }));
      e.target.value = ""; // Reset file input
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      setFileErrors((prev) => ({
        ...prev,
        [fieldId]: "File size should be 1MB or less",
      }));
      e.target.value = ""; // Reset file input
      return;
    }

    setFileErrors((prev) => ({ ...prev, [fieldId]: "" }));

    setSelectedFile(file);
    setStartDate(document.getElementById(`${truckNo}-${key}-start`).value);
    setEndDate(document.getElementById(`${truckNo}-${key}-end`).value);

    setProgress((prev) => {
      console.log("Initialized Progress State:", {
        ...prev,
        [`${truckNo}-${key}`]: 0,
      });
      return { ...prev, [`${truckNo}-${key}`]: 0 };
    });
  };

  const handleStartDateChange = (e, endDate, truckNo, key) => {
    const startDate = e.target.value;
    const fieldId = `${truckNo}-${key}`;

    if (new Date(endDate) < new Date(startDate)) {
      setDateError((prev) => ({
        ...prev,
        [fieldId]: "Start date should be lesser than or equal to the end date.",
      }));
      setStartDate(""); // Clear start date
      return;
    }

    setDateError((prev) => ({ ...prev, [fieldId]: "" }));
    setStartDate(startDate); // Update the start date state
  };

  const handleEndDateChange = (e, startDate, truckNo, key) => {
    const endDate = e.target.value;
    const fieldId = `${truckNo}-${key}`;

    if (new Date(endDate) < new Date(startDate)) {
      setDateError((prev) => ({
        ...prev,
        [fieldId]:
          "End date should be greater than or equal to the start date.",
      }));
      setEndDate(""); // Clear end date
      return;
    }

    setDateError((prev) => ({ ...prev, [fieldId]: "" }));
    setEndDate(endDate); // Update the end date state
  };

  const handleSaveClick = (truckNo, key) => {
    const fieldId = `${truckNo}-${key}`;
    const startDate = document.getElementById(`${truckNo}-${key}-start`).value;
    const endDate = document.getElementById(`${truckNo}-${key}-end`).value;

    // Check for file upload
    if (!selectedFile) {
      setFileErrors("Please select a file to upload.");
      setActiveUploadField(fieldId);
      return;
    }

    // Set active upload field only if the file is uploaded but dates are missing
    if (!startDate || !endDate) {
      setDateError((prev) => ({
        ...prev,
        [fieldId]: "Both start and end dates are required.",
      }));
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      setDateError((prev) => ({
        ...prev,
        [fieldId]:
          "End date should be greater than or equal to the start date.",
      }));
      return;
    }

    // Reset progress state
    setProgress((prev) => {
      console.log("Reset Progress State:", {
        ...prev,
        [`${truckNo}-${key}`]: 0,
      });
      return { ...prev, [`${truckNo}-${key}`]: 0 };
    });

    // Upload file
    handleUploadSubmit(truckNo, key, selectedFile, startDate, endDate);

    // Reset states after upload
    setActiveUploadField(null);
    setSelectedFile(null);
    setStartDate("");
    setEndDate("");
  };

  const filteredVehicles =
    result && Array.isArray(result)
      ? result.filter((vehicle) => {
          const truckNo = vehicle.truck_no.toLowerCase();
          const search = searchTerm.toLowerCase();

          // return [...search].every(char => truckNo.includes(char));
          return truckNo.includes(search);
        })
      : [];

  const handleDelete = async (truck_no) => {
    setDeleteModal({
      isOpen: true,
      recordId: truck_no,
    });
  };

  const confirmDelete = async () => {
    if(userProfile === "admin"){
      console.log("Admin user, deleting vehicle directly.");
      try {
      const response = await axios.delete(
        `${deleteVehicleDataUrl}/${deleteModal.recordId}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        console.log("Vehicle deleted successfully:", deleteModal.recordId);
        showModal("Success", "Vehicle deleted successfully!", "success");
        fetchData(); // Refresh the data
      } else {
        showModal("Error", "Failed to delete Vehicle.", "error");
      }
      setDeleteModal({ isOpen: false, recordId: null });
      // Show success notification (you could add a toast notification system here)
    } catch (error) {
      console.error("Error deleting Vehicle:", error);

      let errorMessage = "Error deleting Vehicle";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showModal("Error", errorMessage, "error");
      setDeleteModal({ isOpen: false, recordId: null });
    }
    } else {
      console.log("User is not admin, sending approval request.");

      const approvalPayload = {
        ...approvalRequest,
        action: "Delete",
        args : {
          truck_no: deleteModal.recordId
        }
      }

      console.log("Approval Payload:", approvalPayload); 
      try {
        const response = await axios.post(
          createRequestUrl,
          {
            approvalRequest: approvalPayload
          },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          console.log("Vehicle delete - approval request created:", deleteModal.recordId);
          showModal("Success", "Approval Request for Vehicle deletion created.!", "success");
          fetchData(); // Refresh the data
        } else {
          showModal("Error", "Failed to create approval request.", "error");
        }
        // Show success notification (you could add a toast notification system here)
      } catch (error) {
        console.error("Error deleting Vehicle:", error);

        let errorMessage = "Error deleting Vehicle";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        showModal("Error", errorMessage, "error");
      }
    }
    setDeleteModal({ isOpen: false, recordId: null });
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // dd/mm/yyyy format
  };

  const handleResetClick = (truckNo, docKey) => {
    setResetModal({
      isOpen: true,
      truckNo: truckNo,
      docKey: docKey,
    });
  };

  const confirmReset = async () => {
    if (userProfile === "admin") {
      console.log("Admin user, resetting document.");
      try {
        const response = await axios.post(
          resetVehicleDocUrl,
          {
            truck_no: resetModal.truckNo,
            field_name: resetModal.docKey,
          },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          showModal("Success", "Document reset successfully!", "success");
          fetchData(); // Refresh the data
        } else {
          showModal("Error", "Failed to reset document.", "error");
        }
        setResetModal({ isOpen: false, truckNo: null, docKey: null });
      } catch (error) {
        console.error("Error resetting document:", error);
        let errorMessage = "Error resetting document";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        showModal("Error", errorMessage, "error");
      }
    } else {
      console.log("User is not an admin, cannot reset document.");

      try {
        const approvalPayload = {
          ...approvalRequest,
          action: "Reset",
          args : {
            truck_no: resetModal.truckNo,
            field_name: resetModal.docKey
          }
        }

        const response = await axios.post(
          createRequestUrl,
          {
            approvalRequest: approvalPayload
          },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.status === 200) {
          console.log("Document reset - approval request created.", response.data);
          showModal("Success", "Vehicle Document reset successfully!", "success");
        } else {
          showModal("Error", "Vehicle Document reset - approval request failed.", "error");
        }
      } catch (error) {
        console.error("Error resetting document:", error);
        let errorMessage = "Error reset request - approval request failed";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        showModal("Error", errorMessage, "error");
      }
    }
  };

  return (
    <div id="vehicle">
      <AlertModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />


      {isLoading && (
        <div className="spinner-container">
          <div className="spinner"></div>
          <span>Loading Vehicle...</span>
        </div>
      )}
      <div className="container-vehicle">
        {/* {!isLoading && (
          <div className="form-wrapper">
            <form
              autoComplete="off"
              className="form-section"
              onSubmit={handleFormSubmit}
            >
              <div className="form-left">
                <div className="filterItem">
                  <label htmlFor="truck_no">Truck No:</label>
                  <input
                    type="text"
                    id="truck_no"
                    name="truck_no"
                    value={formData.truck_no}
                    onChange={handleInputChange}
                    maxLength={10}
                    placeholder="e.g.: MH12KAB1111"
                    required
                  />
                  {errorMessage && (
                    <p style={{ color: "red" }}>{errorMessage}</p>
                  )}
                </div>
                <div className="filterItem">
                  <label htmlFor="make">Make (Truck Company):</label>
                  <input
                    type="text"
                    id="make"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filterItem">
                  <label htmlFor="companyOwner">Company Owner:</label>
                  <input
                    type="text"
                    id="companyOwner"
                    name="companyOwner"
                    value={formData.companyOwner}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-right">
                <table className="field-table">
                  <tbody>
                    {formData.fields.map((field, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td colSpan="3">
                            <label htmlFor={`field-${index}`}>
                              <strong>
                                {keyDisplayMap[field.name] || field.name}{" "}
                                Document:
                              </strong>
                            </label>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <label htmlFor={`field-${index}`}>File:</label>
                            <input
                              ref={(el) => (fileInputRef.current[index] = el)}
                              type="file"
                              id={`field-${index}`}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "file",
                                  e.target.files[0]
                                )
                              }
                              accept=".pdf"
                            />
                            {fileErrors[`field-${index}`] && (
                              <p style={{ color: "red" }}>
                                {fileErrors[`field-${index}`]}
                              </p>
                            )}
                            {progress[field.name] > 0 && (
                              <div className="progress-bar">
                                <div
                                  className="progress-fill"
                                  style={{ width: `${progress[field.name]}%` }}
                                ></div>
                              </div>
                            )}
                          </td>
                          <td>
                            <label htmlFor={`startDate-${index}`}>Start Date:</label>
                            <input
                              type="date"
                              id={`startDate-${index}`}
                              value={field.startDate}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "startDate",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td>
                            <label htmlFor={`endDate-${index}`}>End Date:</label>
                            <input
                              type="date"
                              id={`endDate-${index}`}
                              value={field.endDate}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "endDate",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        </tr>
                        {dateError[`field-${index}`] && (
                          <p style={{ color: "red" }}>
                            {dateError[`field-${index}`]}
                          </p>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="submit-button" type="submit">
                Submit
              </button>
              <p>{submitMessage}</p>
            </form>
          </div>
        )} */}

        
      {/* Card Header with Add Button - Similar to Freight */}
      {!isLoading && (
        <div className="card-header">
          {/* <h2>Vehicles</h2> */}
          
           {!isLoading && (
            <input
              type="text"
              placeholder="Search by Truck Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          )}
          <button className="add-button" onClick={openAddModal}>
            <span className="plus-icon">+</span> Add New Vehicle
          </button>
        </div>
      )}
        
        <div className="table-wrapper">
          {isLoading && (
            <div className="modal-spinner-overlay">
              <div className="spinner"></div>
              <span>Loading vehicle data...</span>
            </div>
          )}

         

          {!isLoading && (
            <div className="tiles-view">
              {filteredVehicles.map((vehicle, index) => (
                <div className="tile" key={index}>
                  <div className="tile-header">
                    <h3>{vehicle.truck_no.split(" ").join("")}</h3>
                    <b>{vehicle.make || ""}</b>
                    <i>{vehicle.companyOwner || ""}</i>
                    <div className="tile-actions">
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(vehicle.truck_no)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <hr />
                  <div className="tile-table">
                    <div className="tile-row tile-header-row">
                      <div className="tile-cell">Field</div>
                      <div className="tile-cell">Validity (days left)</div>
                      <div className="tile-cell">Download</div>
                    </div>
                    {result.length === 0 ? (
                      <tr>
                        <td colSpan="5">No vehicle data found.</td>
                      </tr>
                    ) : (
                      Object.entries(vehicle.documents).map(
                        ([key, doc]) =>
                          doc && (
                            <div className="tile-row" key={key}>
                              <div className="tile-cell">
                                {keyDisplayMap[key] || key}
                              </div>
                              {!uploadFields[`${vehicle.truck_no}-${key}`] &&
                              activeUploadField !==
                                `${vehicle.truck_no}-${key}` ? (
                                <div
                                  className={`tile-cell ${
                                    doc.days_left <= 5 ? "highlighted" : ""
                                  }`}
                                >
                                  {doc.days_left <= 0
                                    ? "(0 days left)"
                                    : `${formatDateDisplay(doc.end_date)} (${
                                        doc.days_left
                                      } days left)`}
                                </div>
                              ) : null}
                              <div className="tile-cell">
                                {doc.days_left <= 0 ? (
                                  <>
                                    {activeUploadField !==
                                    `${vehicle.truck_no}-${key}` ? (
                                      <button
                                        className="upload-button"
                                        onClick={() =>
                                          handleUploadClick(
                                            vehicle.truck_no,
                                            key
                                          )
                                        }
                                      >
                                        <FaUpload className="vehicle-icons" />
                                      </button>
                                    ) : (
                                      <div className="upload-fields">
                                        <div className="form-group">
                                          <label
                                            htmlFor={`${vehicle.truck_no}-${key}-file`}
                                          >
                                            File:
                                          </label>
                                          <input
                                            type="file"
                                            id={`${vehicle.truck_no}-${key}-file`}
                                            onChange={(e) =>
                                              handleFileChange(
                                                e,
                                                vehicle.truck_no,
                                                key
                                              )
                                            }
                                          />
                                          {fileErrors[`field-${index}`] && (
                                            <p style={{ color: "red" }}>
                                              {fileErrors[`field-${index}`]}
                                            </p>
                                          )}
                                          {fileErrors[
                                            `${vehicle.truck_no}-${key}-file`
                                          ] && (
                                            <p style={{ color: "red" }}>
                                              {
                                                fileErrors[
                                                  `${vehicle.truck_no}-${key}-file`
                                                ]
                                              }
                                            </p>
                                          )}
                                        </div>
                                        <div className="form-group">
                                          <label
                                            htmlFor={`${vehicle.truck_no}-${key}-start`}
                                          >
                                            Start Date:
                                          </label>
                                          <input
                                            type="date"
                                            id={`${vehicle.truck_no}-${key}-start`}
                                            defaultValue={startDate}
                                            onChange={(e) =>
                                              handleStartDateChange(
                                                e,
                                                endDate,
                                                vehicle.truck_no,
                                                key
                                              )
                                            }
                                          />
                                        </div>
                                        <div className="form-group">
                                          <label
                                            htmlFor={`${vehicle.truck_no}-${key}-end`}
                                          >
                                            End Date:
                                          </label>
                                          <input
                                            type="date"
                                            id={`${vehicle.truck_no}-${key}-end`}
                                            defaultValue={endDate}
                                            onChange={(e) =>
                                              handleEndDateChange(
                                                e,
                                                startDate,
                                                vehicle.truck_no,
                                                key
                                              )
                                            }
                                          />
                                        </div>
                                        {dateError[
                                          `${vehicle.truck_no}-${key}`
                                        ] && (
                                          <p style={{ color: "red" }}>
                                            {
                                              dateError[
                                                `${vehicle.truck_no}-${key}`
                                              ]
                                            }
                                          </p>
                                        )}
                                        <button
                                          className="save-button"
                                          onClick={() => {
                                            handleSaveClick(
                                              vehicle.truck_no,
                                              key
                                            );
                                          }}
                                        >
                                          Save
                                        </button>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="button-group">
                                    <button
                                      className="download-button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        // Create a hidden anchor element
                                        const link =
                                          document.createElement("a");
                                        link.href = `${config.BASE_URL}/download/${vehicle.truck_no}/${key}`;
                                        link.download = true;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <FaDownload className="vehicle_icons" />
                                    </button>
                                    <button
                                      className="reset-button"
                                      onClick={() =>
                                        handleResetClick(vehicle.truck_no, key)
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      <FaUndo className="vehicle_icons" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              {progress?.[`${vehicle.truck_no}-${key}`] > 0 && (
                                <div className="progress-bar">
                                  <div
                                    className="progress-fill"
                                    style={{
                                      width: `${
                                        progress[
                                          `${vehicle.truck_no}-${key}`
                                        ] || 0
                                      }%`,
                                      transition: "width 0.3s ease-in-out",
                                      backgroundColor: "green",
                                      height: "5px",
                                    }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          )
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

     {/* Add Vehicle Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content vehicle-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              {/* <h3>Add New Vehicle</h3> */}
              
              <button className="close-button" onClick={closeAddModal}>×</button>
            </div>
            
            <form onSubmit={handleFormSubmit} autoComplete="off">
              <div className="modal-body">
                <div className="form-left">
                  <div className="form-group">
                    <label htmlFor="truck_no" className="required">Truck No:</label>
                    <input
                      type="text"
                      id="truck_no"
                      name="truck_no"
                      value={formData.truck_no}
                      onChange={handleInputChange}
                      maxLength={10}
                      placeholder="e.g.: MH12KAB1111"
                      required
                    />
                    {errorMessage && (
                      <p style={{ color: "red", fontSize: "0.85rem", marginTop: "4px" }}>{errorMessage}</p>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="make">Make (Truck Company):</label>
                    <input
                      type="text"
                      id="make"
                      name="make"
                      value={formData.make}
                      onChange={handleInputChange}
                      placeholder="Enter truck make/company"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="companyOwner">Company Owner:</label>
                    <input
                      type="text"
                      id="companyOwner"
                      name="companyOwner"
                      value={formData.companyOwner}
                      onChange={handleInputChange}
                      placeholder="Enter company owner name"
                    />
                  </div>
                </div>

                <div className="documents-section">
                  <h4>Documents</h4>
                  <div className="documents-grid">
                    {formData.fields.map((field, index) => (
                      <div key={index} className="document-item">
                        <label className="document-label">
                          <strong>{keyDisplayMap[field.name] || field.name}:</strong>
                        </label>
                        
                        <div className="document-fields">
                          <div className="file-field">
                            <label>File:</label>
                            <input
                              ref={(el) => (fileInputRef.current[index] = el)}
                              type="file"
                              id={`field-${index}`}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "file",
                                  e.target.files[0]
                                )
                              }
                              accept=".pdf"
                            />
                            {fileErrors[`field-${index}`] && (
                              <p style={{ color: "red", fontSize: "0.8rem" }}>
                                {fileErrors[`field-${index}`]}
                              </p>
                            )}
                          </div>
                          
                          <div className="date-fields">
                            <div className="date-field">
                              <label>Start Date:</label>
                              <input
                                type="date"
                                id={`startDate-${index}`}
                                value={field.startDate}
                                onChange={(e) =>
                                  handleFieldChange(
                                    index,
                                    "startDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            
                            <div className="date-field">
                              <label>End Date:</label>
                              <input
                                type="date"
                                id={`endDate-${index}`}
                                value={field.endDate}
                                onChange={(e) =>
                                  handleFieldChange(
                                    index,
                                    "endDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </div>
                          
                          {dateError[`field-${index}`] && (
                            <p style={{ color: "red", fontSize: "0.8rem" }}>
                              {dateError[`field-${index}`]}
                            </p>
                          )}
                          
                          {progress[field.name] > 0 && (
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${progress[field.name]}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {submitMessage && (
                  <p className={`submit-message ${submitMessage.includes("Error") ? "error" : "success"}`}>
                    {submitMessage}
                  </p>
                )}
              </div>
              
              <div className="modal-footer">
                <button type="button" className="vehicle-cancel-button" onClick={closeAddModal}>
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal.isOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="54"
                height="54"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  fill="#ff4757"
                  d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                />
              </svg>
            </div>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this Vehicle:{" "}
              <span className="trip-id-highlight">{deleteModal.recordId}</span>?
            </p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-modal-buttons">
              <button
                className="cancel-btn"
                onClick={() =>
                  setDeleteModal({ isOpen: false, recordId: null })
                }
              >
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reset modale */}
      {resetModal.isOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="54"
                height="54"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  fill="#ff9800"
                  d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                />
              </svg>
            </div>
            <h3>Confirm Reset</h3>
            <p>
              Are you sure you want to reset{" "}
              {keyDisplayMap[resetModal.docKey] || resetModal.docKey} document
              for vehicle:{" "}
              <span className="trip-id-highlight">{resetModal.truckNo}</span>?
            </p>
            <p className="warning-text">
              This will delete the document and set validity to 0 days.
            </p>
            <div className="delete-modal-buttons">
              <button
                className="cancel-btn"
                onClick={() =>
                  setResetModal({ isOpen: false, truckNo: null, docKey: null })
                }
              >
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicle;